import React, { useState } from 'react';
import styled from 'styled-components';
import { VideoComment } from '../../types/video';

interface VideoCommentsProps {
  comments: VideoComment[];
  videoId: string;
  onAddComment: (videoId: string, content: string) => void;
}

const CommentsContainer = styled.div`
  background-color: #1C1628;
  border-radius: 12px;
  padding: 16px;
  max-height: 400px;
  overflow-y: auto;
`;

const CommentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const CommentTitle = styled.h3`
  color: #fff;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
`;

const CommentCount = styled.span`
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
`;

const CommentForm = styled.form`
  display: flex;
  margin-bottom: 24px;
  gap: 8px;
`;

const CommentInput = styled.input`
  flex: 1;
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 12px 16px;
  color: #fff;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #9333EA;
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const CommentButton = styled.button`
  background-color: #9333EA;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    background-color: #7C3AED;
  }
  
  &:disabled {
    background-color: rgba(147, 51, 234, 0.5);
    cursor: not-allowed;
  }
`;

const CommentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CommentItem = styled.div`
  display: flex;
  gap: 12px;
`;

const CommentAvatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
`;

const CommentContent = styled.div`
  flex: 1;
`;

const CommentUser = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`;

const CommentUserName = styled.span`
  color: #fff;
  font-size: 14px;
  font-weight: 500;
`;

const CommentTime = styled.span`
  color: rgba(255, 255, 255, 0.4);
  font-size: 12px;
`;

const CommentText = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  line-height: 1.4;
  margin: 0;
`;

const CommentActions = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 8px;
`;

const CommentAction = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  padding: 0;
  
  &:hover {
    color: #fff;
  }
`;

const EmptyComments = styled.div`
  text-align: center;
  padding: 24px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
`;

const VideoComments: React.FC<VideoCommentsProps> = ({
  comments,
  videoId,
  onAddComment
}) => {
  const [commentText, setCommentText] = useState('');
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (commentText.trim()) {
      onAddComment(videoId, commentText.trim());
      setCommentText('');
    }
  };
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) {
      return `${diffInSeconds}s ago`;
    }
    
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    }
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    }
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) {
      return `${diffInDays}d ago`;
    }
    
    return date.toLocaleDateString();
  };
  
  return (
    <CommentsContainer>
      <CommentHeader>
        <CommentTitle>Comments</CommentTitle>
        <CommentCount>{comments.length}</CommentCount>
      </CommentHeader>
      
      <CommentForm onSubmit={handleSubmit}>
        <CommentInput
          type="text"
          placeholder="Add a comment..."
          value={commentText}
          onChange={(e) => setCommentText(e.target.value)}
        />
        <CommentButton type="submit" disabled={!commentText.trim()}>
          Post
        </CommentButton>
      </CommentForm>
      
      {comments.length === 0 ? (
        <EmptyComments>No comments yet. Be the first to comment!</EmptyComments>
      ) : (
        <CommentList>
          {comments.map((comment) => (
            <CommentItem key={comment.id}>
              <CommentAvatar src={comment.userAvatar} alt={comment.userName} />
              <CommentContent>
                <CommentUser>
                  <CommentUserName>{comment.userName}</CommentUserName>
                  <CommentTime>{formatDate(comment.createdAt)}</CommentTime>
                </CommentUser>
                <CommentText>{comment.content}</CommentText>
                <CommentActions>
                  <CommentAction>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                    </svg>
                    {comment.likes}
                  </CommentAction>
                  <CommentAction>Reply</CommentAction>
                </CommentActions>
              </CommentContent>
            </CommentItem>
          ))}
        </CommentList>
      )}
    </CommentsContainer>
  );
};

export default VideoComments;
